import { Suspense, useCallback, useMemo, useRef, useState } from 'react';
import { useMeApi } from '~/lib/api/me/use-me-api';
import { P2GButton, P2GGrid, P2GText, P2GTextBox, useP2GMediaQueryOnly } from '@parcel2go/core-ui';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { GetPrePayBalanceResponse } from '~/lib/api/me/types';
import { usePaymentApi } from '~/lib/api/payment/use-payment-api';
import { ContentGroups, TranslationNamespaces } from '~/lib/constants';
import { Trans, useTranslation } from 'react-i18next';
import PrePayBalanceDisplay from './prepay-balance-display';
import useOnLoad from '~/routes/use-on-load';
import StandaloneCmsContent from '~/components/content/standalone-cms-content';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { EventSections } from '~/apps/smartsend/lib/events/EventLogger';

const QUICK_TOPUP_OPTIONS: Record<string, number[]> = {
  INR: [2000, 5000, 10000, 25000],
  THB: [1000, 2500, 4500, 11000]
};

const DEFAULT_QUICK_TOPUP_OPTIONS = [20, 50, 100, 250];

/** Marketing-managed card / PayPal Pay-Later logos shown beneath the top-up amount. */
const PAYMENT_LOGOS_CONTENT_KEY = 'content-prepay-topup-payment-logos';

/** Which top-up control the customer used, carried through to the payment link events. */
type TopupSource = 'quick-select' | 'custom';

interface props {
  /** Where the component is rendered - the /prepay page or the nav side panel. Analytics only. */
  surface?: 'page' | 'sidebar';
}

const PrePayTopup = ({ surface = 'page' }: props) => {
  const { generatePrePayLink } = usePaymentApi();
  const { t } = useTranslation(TranslationNamespaces.PREPAY);
  const { getPrePayBalance } = useMeApi();
  const { track } = useSmartSendAnalytics();
  const [topup, setTopup] = useState<number>(0);
  const [balance, setBalance] = useState<GetPrePayBalanceResponse>();
  const isXs = useP2GMediaQueryOnly('xs');
  const [loadingTopup, setLoadingTopup] = useState<boolean>(false);
  const lastTrackedCustomAmount = useRef<number | null>(null);

  const minTopup = balance?.prePayMinimumTopUp ?? 0;

  const trackTopup = useCallback(
    (type: string, payload: Record<string, unknown> = {}) => {
      track({
        type,
        payload: {
          ...GenerateBaseAnalyticsPayload([EventSections.PrePayTopup]),
          surface,
          currencyCode: balance?.currencyCode,
          ...payload
        }
      });
    },
    [track, surface, balance?.currencyCode]
  );

  useOnLoad(() => {
    const load = async () => {
      const balanceResponse = await getPrePayBalance();
      if (balanceResponse.success && balanceResponse.result) {
        const result = balanceResponse.result;
        setBalance(result);
        // Tracked here rather than on mount so the payload holds real values, not undefined.
        track({
          type: 'prepay_topup_loaded',
          payload: {
            ...GenerateBaseAnalyticsPayload([EventSections.PrePayTopup]),
            surface,
            currencyCode: result.currencyCode,
            balance: result.balance,
            minTopup: result.prePayMinimumTopUp,
            quickOptions: quickTopupOptions(result),
            freePrePayPercentage: result.freePrePayPercentage,
            freePrePayMinimumSpend: result.freePrePayMinimumSpend,
            hasPromotion: (result.freePrePayPercentage ?? 0) > 0
          }
        });
      }
    };
    load();
  });

  const doTopup = useCallback(
    async (topupAmount: number, source: TopupSource) => {
      setLoadingTopup(true);
      const res = await generatePrePayLink(topupAmount * 100, balance?.currencyCode ?? 'GBP');
      if (res.success) {
        // Must precede the redirect - sendBeacon survives navigation, but ordering does not.
        trackTopup('prepay_topup_link_created', { amount: topupAmount, source, orderId: res.result.orderId });
        window.location.href = res.result.link;
      } else {
        trackTopup('prepay_topup_link_failed', { amount: topupAmount, source });
        setLoadingTopup(false);
      }
    },
    [generatePrePayLink, balance, trackTopup]
  );

  const quickOptions = useMemo(() => quickTopupOptions(balance), [balance]);

  return (
    <P2GGrid container direction="column" spacing={2}>
      {balance && (
        <>
          <P2GGrid item>
            <PrePayBalanceDisplay balance={balance} includePromotion={true} />
          </P2GGrid>
          <P2GGrid item>
            <P2GText component="p" mb={1}>
              <strong>{t('QUICK_SELECT_AMOUNT', { defaultValue: 'Quick Select Topup Amount' })}</strong>
            </P2GText>
            {quickOptions.length > 0 && (
              <P2GGrid container spacing={1}>
                {quickOptions.map((amt) => (
                  <P2GGrid item key={amt} sm="auto">
                    <P2GButton
                      variant={amt === topup ? 'secondary' : 'tertiary'}
                      onClick={() => {
                        setTopup(amt);
                        // The text box is bound to `topup`, so claim this amount for the quick-select
                        // path - otherwise a later blur (e.g. after a failed link) would re-report it
                        // as a custom entry.
                        lastTrackedCustomAmount.current = amt;
                        trackTopup('prepay_topup_quick_amount_click', {
                          amount: amt,
                          source: 'quick-select',
                          meetsPromotionThreshold: meetsPromotionThreshold(amt, balance)
                        });
                        doTopup(amt, 'quick-select');
                      }}
                      disabled={loadingTopup}
                      fullWidth
                    >
                      {toDisplayCurrency(balance?.currencyCode ?? 'GBP', amt, 0)}
                    </P2GButton>
                  </P2GGrid>
                ))}
              </P2GGrid>
            )}
          </P2GGrid>
        </>
      )}
      <P2GGrid item>
        <P2GText component="p" mb={1}>
          <strong>{t('OR_ENTER_CUSTOM_AMOUNT', { defaultValue: 'Or Enter Custom Amount' })}</strong>
        </P2GText>
        <P2GTextBox
          id="PrePayAmount"
          required
          label={t('ADD_CREDIT_INPUT_LABEL', { defaultValue: 'Topup Amount' })}
          value={topup == 0 ? '' : topup}
          onChange={(e) => setTopup(Number(e))}
          onBlur={() => {
            // Report the amount the customer settled on, not every keystroke.
            if (!topup || lastTrackedCustomAmount.current === topup) {
              return;
            }
            lastTrackedCustomAmount.current = topup;
            trackTopup('prepay_topup_custom_amount_entered', { amount: topup, belowMinimum: topup < minTopup });
          }}
          min={minTopup}
          type="number"
          fullWidth
          endAdornment={
            <P2GButton
              variant="primary"
              disabled={!topup || topup < minTopup || loadingTopup}
              fullWidth={isXs}
              onClick={() => {
                trackTopup('prepay_topup_submit_click', { amount: topup, source: 'custom' });
                doTopup(topup, 'custom');
              }}
            >
              {loadingTopup
                ? t('LOADING', { defaultValue: 'Loading...' })
                : t('ADD_CREDIT_BUTTON', { defaultValue: 'Add Credit' })}
            </P2GButton>
          }
        />
      </P2GGrid>
      <Suspense>
        {/* Wrapped rather than nested in a P2GGrid item so no spacing gap is left when unpublished. */}
        <StandaloneCmsContent
          group={ContentGroups.PAYMENTS}
          contentKey={PAYMENT_LOGOS_CONTENT_KEY}
          wrapper={(c) => <P2GGrid item>{c}</P2GGrid>}
        />
      </Suspense>
      {balance && (
        <P2GGrid item>
          <P2GText variant="caption">
            <Trans
              i18nKey="MINIMUM_TOPUP"
              t={t}
              values={{ minAmount: toDisplayCurrency(balance.currencyCode, minTopup, 0) }}
              components={{ b: <strong /> }}
              defaults="* Prepayments must be for a minimum of <b>{{minAmount}}</b>."
            />
          </P2GText>
        </P2GGrid>
      )}
    </P2GGrid>
  );
};

/** Quick-select amounts for the balance's currency, dropping any below the minimum top-up. */
function quickTopupOptions(balance: GetPrePayBalanceResponse | undefined) {
  const options = QUICK_TOPUP_OPTIONS[balance?.currencyCode] ?? DEFAULT_QUICK_TOPUP_OPTIONS;
  return options.filter((amount) => amount >= (balance?.prePayMinimumTopUp ?? 0));
}

/** Whether an amount qualifies for the free-credit promotion, when one is running. */
function meetsPromotionThreshold(amount: number, balance: GetPrePayBalanceResponse | undefined) {
  if (!balance?.freePrePayPercentage || !balance.freePrePayMinimumSpend) {
    return false;
  }
  return amount >= balance.freePrePayMinimumSpend;
}

export default PrePayTopup;
